import { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { getCookie } from '../../utils/utils';
import TagManager from 'react-gtm-module';

export interface SellListing {
  _id: string;
  building_id?: string;
  address_string: string;
  address?: {
    street?: string;
    houseNumber?: string;
    city?: string;
    country?: string;
    postcode?: string;
  };
  images?: Array<{
    id?: string;
    url: string;
    thumbnailUrl?: string;
  }>;
  property_description?: string;
  property_size?: number;
  property_rooms?: string;
  property_floor?: number;
  property_has_elevator?: boolean;
  property_has_balcony?: boolean;
  property_renovation_status?: string;
  property_asking_price?: number;
  updated_at?: string;
  verified?: boolean;
}

export interface UseTipBuyerProps {
  buyWillingnessId: string;
  isLoggedIn?: boolean;
  onTipBuyerMenuToggle?: (isOpen: boolean) => void;
  setLoginModalVisible?: (visible: boolean) => void;
}

export interface UseTipBuyerReturn {
  // State
  showMenu: boolean;
  userSellListings: SellListing[];
  isLoadingListings: boolean;
  selectedListing: SellListing | null;
  showPreviewModal: boolean;
  isSendingTip: boolean;
  tippedListings: Set<string>;
  tipSentSuccess: boolean;
  dailyLimitReached: boolean;
  tipsRemaining: number | null;
  menuOpensDownward: boolean;
  
  // Refs
  menuRef: React.RefObject<HTMLDivElement>;
  buttonRef: React.RefObject<HTMLButtonElement>;
  
  // Handlers
  handleButtonClick: (e: React.MouseEvent) => void;
  handleSelectListing: (listing: SellListing) => void;
  handleConfirmTip: () => Promise<void>;
  closeMenu: () => void;
  closePreviewModal: () => void;
}

export const useTipBuyer = ({
  buyWillingnessId,
  isLoggedIn,
  onTipBuyerMenuToggle,
}: UseTipBuyerProps): UseTipBuyerReturn => {
  const { t } = useTranslation();
  // State
  const [showMenu, setShowMenu] = useState(false);
  const [userSellListings, setUserSellListings] = useState<SellListing[]>([]);
  const [isLoadingListings, setIsLoadingListings] = useState(false);
  const [selectedListing, setSelectedListing] = useState<SellListing | null>(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [isSendingTip, setIsSendingTip] = useState(false);
  const [tippedListings, setTippedListings] = useState<Set<string>>(new Set());
  const [tipSentSuccess, setTipSentSuccess] = useState(false);
  const [dailyLimitReached, setDailyLimitReached] = useState(false);
  const [tipsRemaining, setTipsRemaining] = useState<number | null>(null);
  const [menuOpensDownward, setMenuOpensDownward] = useState(false);

  // Refs
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuMounted = useRef(false);

  // Notify parent when menu opens (for hiding mobile header/footer)
  useEffect(() => {
    if (menuMounted.current && onTipBuyerMenuToggle && showMenu) {
      onTipBuyerMenuToggle(true);
    }
    menuMounted.current = true;
  }, [showMenu, onTipBuyerMenuToggle]);

  // Handle click outside to close menu
  useEffect(() => {
    const handleClickOutside = (event: Event) => {
      if (
        showMenu &&
        menuRef.current &&
        buttonRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setShowMenu(false);
        setMenuOpensDownward(false);
      }
    };

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMenu]);

  // Handler for button click
  const handleButtonClick = useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    if (showMenu) {
      setShowMenu(false);
      setMenuOpensDownward(false);
      return;
    }

    setShowMenu(true);

    // Calculate menu position - check if there's enough space above
    requestAnimationFrame(() => {
      if (buttonRef.current) {
        const buttonRect = buttonRef.current.getBoundingClientRect();
        const menuHeight = 280; // Approximate max menu height
        const spaceAbove = buttonRect.top;
        
        // If not enough space above, open downward
        setMenuOpensDownward(spaceAbove < menuHeight + 20);
      }
    });

    // If not logged in, just show the menu with login prompt
    if (!isLoggedIn) {
      return;
    }

    // Prevent multiple simultaneous requests
    if (isLoadingListings) {
      return;
    }

    setIsLoadingListings(true);

    try {
      // First, check the daily limit
      const limitResponse = await fetch(
        `${process.env.REACT_APP_API_URL}/tip-buyer/daily-limit`,
        {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': getCookie('csrf_access_token'),
          },
        }
      );

      let limitReached = true;
      if (limitResponse.ok) {
        const limitData = await limitResponse.json();
        limitReached = Boolean(limitData.limit_reached);
        setDailyLimitReached(limitReached);
        // max_tips of -1 means unlimited (admin)
        if (limitData.max_tips === -1) {
          setTipsRemaining(null);
        } else {
          setTipsRemaining(Math.max(0, limitData.max_tips - limitData.tips_today));
        }
      }

      // Then fetch listings
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/user/sell-listings`,
        {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch listings');
      }

      const data = await response.json();
      const listings = data.listings || [];
      setUserSellListings(listings);

      // Check which listings have already been tipped
      let tipped = new Set<string>();
      if (listings.length > 0) {
        const checkPromises = listings.map((listing: SellListing) =>
          fetch(`${process.env.REACT_APP_API_URL}/tip-buyer/check`, {
            method: 'POST',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRF-TOKEN': getCookie('csrf_access_token'),
            },
            body: JSON.stringify({
              sell_willingness_id: listing._id,
              buy_willingness_id: buyWillingnessId,
            }),
          }).then(res => res.ok ? res.json() : { already_tipped: false })
            .then(checkData => ({ listingId: listing._id, tipped: checkData.already_tipped }))
            .catch(() => ({ listingId: listing._id, tipped: false }))
        );

        const results = await Promise.all(checkPromises);
        results.forEach(result => {
          if (result.tipped) {
            tipped.add(result.listingId);
          }
        });
      }
      setTippedListings(tipped);

      const onlyListing = listings.length === 1 ? listings[0] : null;
      if (onlyListing && !limitReached && !tipped.has(onlyListing._id)) {
        setSelectedListing(onlyListing);
        setShowPreviewModal(true);
        setShowMenu(false);
        setMenuOpensDownward(false);
      }
    } catch (error) {
      console.error('Error fetching sell listings:', error);
    } finally {
      setIsLoadingListings(false);
    }
  }, [showMenu, isLoggedIn, isLoadingListings, buyWillingnessId]);

  // Handler for selecting a listing
  const handleSelectListing = useCallback((listing: SellListing) => {
    if (dailyLimitReached) {
      return;
    }
    setSelectedListing(listing);
    setShowPreviewModal(true);
    setShowMenu(false);
  }, [dailyLimitReached]);

  // Handler for confirming and sending the tip
  const handleConfirmTip = useCallback(async () => {
    if (!selectedListing) return;

    setIsSendingTip(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/tip-buyer`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': getCookie('csrf_access_token'),
          },
          body: JSON.stringify({
            sell_willingness_id: selectedListing._id,
            buy_willingness_id: buyWillingnessId,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        if (response.status === 409) {
          alert(t('tipBuyer.alreadyTipped', 'You have already tipped this buyer about this listing'));
        } else {
          throw new Error(errorData.error || 'Failed to send tip');
        }
        return;
      }

      // Success - mark this listing as tipped and show success celebration
      setTippedListings(prev => new Set(prev).add(selectedListing._id));
      setTipSentSuccess(true);

      // Auto-close after celebration (2.5 seconds)
      setTimeout(() => {
        setShowPreviewModal(false);
        setTipSentSuccess(false);
        setSelectedListing(null);
      }, 2500);

      // Track the event
      TagManager.dataLayer({
        dataLayer: {
          event: 'tip_buyer_sent',
          category: 'User Interaction',
          action: 'Click',
          label: 'Tip Buyer',
          buy_interest_id: buyWillingnessId,
          sell_listing_id: selectedListing._id,
        },
      });
    } catch (error) {
      console.error('Error sending tip:', error);
      alert(t('common.error', 'An error occurred. Please try again.'));
    } finally {
      setIsSendingTip(false);
    }
  }, [selectedListing, buyWillingnessId, t]);

  // Close handlers
  const closeMenu = useCallback(() => {
    setShowMenu(false);
    setMenuOpensDownward(false);
  }, []);

  const closePreviewModal = useCallback(() => {
    setShowPreviewModal(false);
    setTipSentSuccess(false);
    setSelectedListing(null);
  }, []);

  return {
    // State
    showMenu,
    userSellListings,
    isLoadingListings,
    selectedListing,
    showPreviewModal,
    isSendingTip,
    tippedListings,
    tipSentSuccess,
    dailyLimitReached,
    tipsRemaining,
    menuOpensDownward,
    
    // Refs
    menuRef,
    buttonRef,
    
    // Handlers
    handleButtonClick,
    handleSelectListing,
    handleConfirmTip,
    closeMenu,
    closePreviewModal,
  };
};
